import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import DefaultHelpWidget from "./DefaultHelpWidget";

import {
  type FreshdeskContext,
  hideFreshChatButton,
  initFreshChat,
  loadFreshChat,
  showFreshChatButton,
} from "./freshdeskApi";
import {
  makeFreshDeskWidgetPositionStyles,
  type Position,
} from "./positioning";
import { matchRoutes, type Route } from "./routeMatching";

interface Props {
  /**
   * A list of routes the widget should be shown on.
   *
   * If `exact` is `true` then we will only show the widget where the current location matches exactly the `path`, otherwise we will show it for any location that contains the `path`.
   */
  activeRoutes: Route[];
  freshdeskToken?: string;
  freshdeskHost?: string;
  positionOverrides?: (Position & { routes: Route[] })[];
  context: FreshdeskContext;
}

const Freshdesk: FC<Props> = ({
  activeRoutes,
  freshdeskToken,
  freshdeskHost,
  positionOverrides,
  context,
}) => {
  const { pathname } = useLocation();
  const [freshdeskLoaded, setLoaded] = useState(false);
  const [freshdeskFailedToLoad, setFreshdeskFailedToLoad] = useState(false);
  const [freshdeskInited, setFreshdeskInited] = useState(false);
  const [freshdeskWidgetLoaded, setFreshdeskWidgetLoaded] = useState(false);

  // Adds Freshdesk <script>
  useEffect(() => {
    if (freshdeskHost && !freshdeskInited) {
      loadFreshChat({
        freshdeskHost,
        onLoad: () => setLoaded(true),
        onError: () => setFreshdeskFailedToLoad(true),
      });
      setFreshdeskInited(true);
    }
  }, [freshdeskHost, freshdeskInited]);

  const freshdeskReadyToLoad =
    !!freshdeskLoaded && !!freshdeskToken && !!freshdeskHost && !!context?.user;

  const widgetShouldBeActiveOnLocation = matchRoutes(pathname, activeRoutes);

  // on each route change inits/deinits Freshdesk
  useEffect(() => {
    if (!freshdeskReadyToLoad) return;

    if (widgetShouldBeActiveOnLocation && !freshdeskWidgetLoaded) {
      initFreshChat(
        {
          freshdeskToken,
          freshdeskHost,
          onWidgetLoaded: () => setFreshdeskWidgetLoaded(true),
        },
        context,
      );
      return;
    }

    // Check if the widget is loaded & route before showing
    if (freshdeskWidgetLoaded && widgetShouldBeActiveOnLocation) {
      showFreshChatButton();
      return;
    }

    hideFreshChatButton();
  }, [
    freshdeskToken,
    freshdeskHost,
    widgetShouldBeActiveOnLocation,
    freshdeskReadyToLoad,
    freshdeskWidgetLoaded,
    context,
  ]);

  const currentPositionOverrides = positionOverrides?.find((p) =>
    matchRoutes(pathname, p.routes),
  );

  // Useful for sponsored data
  if (freshdeskFailedToLoad && widgetShouldBeActiveOnLocation) {
    return (
      <DefaultHelpWidget
        territory={context.territory}
        positionOverrides={currentPositionOverrides}
      />
    );
  }

  return currentPositionOverrides ? (
    <style>{makeFreshDeskWidgetPositionStyles(currentPositionOverrides)}</style>
  ) : null;
};

export { Freshdesk };
